import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { format } from 'date-fns-tz';
import { compact, upperCase } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';
import { RktFormComponent, formRadioGroup, formSelect } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

import { MoveHowSelection } from '@shared/components/move-money';
import { LinxProfileType, TransactionOccurrenceType } from '@shared/constants';
import {
  AdaRegistration,
  AdaRegistrationList,
  FinancialAccountList,
  FinancialAccountListItem,
  FormModel,
  LinxRequestFinancialAccount,
  LinxRequestFinancialAccountType,
} from '@shared/models';
import { AdaRegistrationService, FinancialAccountService } from '@shared/services';
import { ErrorUtils, systemTimeZone } from '@shared/utils';

@Component({
  selector: 'app-move-money-financial-account',
  templateUrl: './move-money-financial-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, ReactiveFormsModule, RktFormComponent, RktIconComponent],
})
export class MoveMoneyFinancialAccountComponent implements OnInit, AfterViewChecked, OnDestroy {
  readonly financialAccountService = inject(FinancialAccountService);
  readonly ref = inject(ChangeDetectorRef);
  readonly adaRegistrationService = inject(AdaRegistrationService);

  profileType = input<LinxProfileType>();

  moveHowSelection = input<MoveHowSelection>();

  accountHolderId = input<string>('');

  continue = output<LinxRequestFinancialAccount | undefined>();

  financialAccountForm = new UntypedFormGroup({});

  isTemplate = input<boolean>(false);

  formModel: FormModel<{
    financialAccountType?: LinxRequestFinancialAccountType;
    financialAccountSubType?: string;
    existingFinancialAccountId?: string;
    debitAuthorizationType?: 'NEW' | 'EXISTING' | 'OFFLINE';
    debitAuthorizationId?: string;
  }> = {};

  financialAccountOptions = signal<Record<string, string | boolean>[]>([]);

  financialAccountFields = computed(() => {
    return [
      formRadioGroup({
        key: 'financialAccountType',
        label: '',
        props: {
          options: [
            { label: 'Use Existing Financial Account', value: LinxRequestFinancialAccountType.EXISTING },
            { label: 'Create New Financial Account', value: LinxRequestFinancialAccountType.NEW },
            { label: 'Select or Create financial account', value: LinxRequestFinancialAccountType.NEW_OR_EXISTING },
          ],
          required: true,
        },
        expressions: {
          hide: () => this.isNewAccount,
        },
      }),
      formRadioGroup({
        key: 'financialAccountSubType',
        label: 'Account Type',
        props: {
          options: [
            { label: 'Checking Account', value: 'CHECKING', disabled: this.moveHowSelection()?.moveHowType === 'push-to-card' },
            { label: 'Saving Account', value: 'SAVING', disabled: this.moveHowSelection()?.moveHowType === 'push-to-card' },
            { label: 'Card Account', value: 'CARD', disabled: ['ach', 'wire'].includes(this.moveHowSelection()?.moveHowType ?? '') },
          ],
          required: true,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) =>
            field.model.financialAccountType !== LinxRequestFinancialAccountType.NEW && !this.isNewAccount,
        },
      }),
      formSelect({
        key: 'existingFinancialAccountId',
        label: 'Financial Account',
        props: {
          options: this.financialAccountOptions(),
          placeholder: 'Select a Financial Account',
          required: true,
          change: () => this.getRegistrationsForFinancialAccount(),
        },
        className: 'linx-form-field-dropdown',
        expressions: {
          hide: (field: FormlyFieldConfig) =>
            this.isNewAccount || field.model.financialAccountType !== LinxRequestFinancialAccountType.EXISTING || this.isTemplate(),
        },
      }),
    ];
  });

  debitAuthorizationForm = new UntypedFormGroup({});

  debitAuthorizationField: FormlyFieldConfig[] = [
    formRadioGroup({
      key: 'debitAuthorizationType',
      label: '',
      className: 'linx-half-form-field',
      props: {
        options: compact([
          { label: 'Capture new authorization', value: 'NEW' },
          { label: 'Existing authorization', value: 'EXISTING' },
          { label: 'Offline authorization', value: 'OFFLINE' },
        ]),
        required: true,
      },
    }),
    formSelect({
      key: 'debitAuthorizationId',
      label: 'Existing Debit Authorization',
      props: {
        options: [],
        placeholder: 'Select a Debit Authorization',
        required: true,
      },
      className: 'linx-form-field-dropdown',
      expressions: {
        hide: (field: FormlyFieldConfig) => this.isNewAccount || field.model.debitAuthorizationType !== 'EXISTING' || this.isTemplate(),
      },
    }),
  ];

  get showDebitAuthorization(): boolean {
    if (this.moveHowSelection()?.moveDirection === 'push') return false;
    if (this.moveHowSelection()?.moveHowType !== 'ach') return false;

    if (this.formModel.existingFinancialAccountId) {
      const existingFinancialAccount = this.financialAccounts?.find((item) => item.id === this.formModel.existingFinancialAccountId);
      return existingFinancialAccount?.category === 'EXTERNAL' && existingFinancialAccount.type === 'BANK';
    }

    return this.financialAccountForm.valid;
  }

  financialAccountsLoading = false;

  financialAccounts!: FinancialAccountListItem[];
  debitAuthorizations?: AdaRegistration[];

  get isNewAccount(): boolean {
    return this.profileType() === LinxProfileType.New;
  }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (!this.isNewAccount && !this.isTemplate()) {
      this.getFinancialAccounts();
    }

    this.financialAccountForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.debitAuthorizationForm.reset();
      this.formModel.debitAuthorizationId = undefined;
      this.formModel.debitAuthorizationType = undefined;
    });
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFinancialAccountDetailsChange(): void {
    this.continue.emit(undefined);

    if (!this.financialAccountForm.valid) return;
    if (this.showDebitAuthorization && !this.debitAuthorizationForm.valid) return;

    const {
      financialAccountType = LinxRequestFinancialAccountType.NEW,
      financialAccountSubType,
      existingFinancialAccountId,
      debitAuthorizationId,
    } = this.formModel;

    const formData = { financialAccountType } as LinxRequestFinancialAccount;

    if (financialAccountType === LinxRequestFinancialAccountType.EXISTING && !this.isTemplate()) {
      if (!existingFinancialAccountId) return;

      const existingFinancialAccount = this.financialAccounts.find((item) => item.id === existingFinancialAccountId);
      formData.existingFinancialAccount = existingFinancialAccount;
      formData.financialAccountSubType = existingFinancialAccount?.subtype;
    } else if (financialAccountType === LinxRequestFinancialAccountType.NEW) {
      if (!financialAccountSubType) return;

      formData.financialAccountSubType = financialAccountSubType;
    }

    if (this.formModel.debitAuthorizationType) {
      formData.debitAuthorization = {
        debitAuthorizationType: this.formModel.debitAuthorizationType,
      };
      if (this.formModel.debitAuthorizationType === 'NEW') {
        if (!this.isTemplate()) {
          formData.debitAuthorization.timeZone = systemTimeZone;
          formData.debitAuthorization.startDate = format(new Date(), 'yyyy-MM-dd');
        }
        formData.debitAuthorization.occurrenceType = TransactionOccurrenceType.SINGLE;
      } else if (this.formModel.debitAuthorizationType === 'OFFLINE') {
        formData.debitAuthorization.offlineAuthorization = true;
      } else if (debitAuthorizationId) {
        const adaRegistration = this.debitAuthorizations?.find((authorization) => authorization.id === debitAuthorizationId);
        const { transactionOccurrenceType, paymentFrequency, startDate, endDate, timeZone, currency } = adaRegistration ?? {};
        formData.debitAuthorization = {
          ...formData.debitAuthorization,
          debitAuthorizationId: debitAuthorizationId,
          currency: currency,
          occurrenceType: transactionOccurrenceType,
          paymentFrequency: paymentFrequency,
          startDate: startDate,
          endDate: endDate,
          timeZone: timeZone,
        };
      }
      formData.verifyFinancialAccount = true;
    }

    this.continue.emit(formData);
  }

  onClearBtnClick(): void {
    this.financialAccountForm.reset();
  }

  getFinancialAccounts(): void {
    if (!this.accountHolderId()) {
      return;
    }

    this.financialAccountService
      .getCustomerFinancialAccounts({ customerId: this.accountHolderId() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.onFetchComplete(response);
        },
        error: (error) => {
          this.onFetchComplete();
          ErrorUtils.catchError('customerService.getCustomerFinancialAccounts', error);
        },
      });
  }

  onFetchComplete(response?: FinancialAccountList): void {
    const { items = [] } = response || {};

    this.financialAccounts = items;

    if (items.length) {
      const financialAccountOptions: Record<string, string | boolean>[] = [];

      items.forEach((item) => {
        const availableBalance = item.availableBalance ? item.availableBalance : '0.00';
        const account = {
          value: item.id,
          label: `${item.name ?? item.displayName} | Available Balance $ ${availableBalance}`,
        };
        financialAccountOptions.push(account);
      });
      this.financialAccountOptions.set(financialAccountOptions);
    }

    this.financialAccountsLoading = false;
  }

  getRegistrationsForFinancialAccount(): void {
    if (this.debitAuthorizationField[1]?.props) {
      this.debitAuthorizationField[1].props.options = [];
    }

    if (!this.formModel.existingFinancialAccountId) return;

    this.adaRegistrationService
      .getAdaRegistrations({
        page: 0,
        size: 100,
        activeFilters: {
          financialAccountIds: this.formModel.existingFinancialAccountId,
        },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (debitRegistrations) => {
          this.mapADARegistrationDropdown(debitRegistrations);
        },
        error: (error) => {
          ErrorUtils.catchError('adaRegistrationService.getAdaRegistrations', error);
        },
      });
  }

  mapADARegistrationDropdown(debitAuthorizations: AdaRegistrationList): void {
    const { items } = debitAuthorizations;

    this.debitAuthorizations = items;

    const mappedItems = items?.length
      ? items.map((adaRegistration) => ({
          label: `${upperCase(adaRegistration.transactionOccurrenceType)}: ${
            adaRegistration.amount ? '$' + adaRegistration.amount : 'AMT UNSPECIFIED'
          }`,
          value: adaRegistration.id,
        }))
      : [];

    if (this.debitAuthorizationField[1]?.props) {
      this.debitAuthorizationField[1].props.options = mappedItems;
    }
  }
}
